import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from "react";
import {
  Users,
  Eye,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  Filter,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Consultation,
  consultationTypes,
  useConsultationsState,
} from "@/lib/dashboard/consultationsData";
import { ConsultationSummaryModal } from "./ConsultationSummaryModal";
import Skeleton from "@mui/material/Skeleton";
import Box from "@mui/material/Box";

interface RecentConsultationsProps {
  consultations: Consultation[];
  isDarkMode: boolean;
  onSelectConsultation: (consultation: Consultation) => void;
}

export function RecentConsultations({
  consultations,
  isDarkMode,
  onSelectConsultation,
}: RecentConsultationsProps) {
  const {
    showPatientConsultationsLoader,
    setStreamedText,
    setSnomedCodes,
    summarizeConsultationSummaries,
    isConsultationPointsLoading,
    resetConsultationsState,
  } = useConsultationsState();
  const [selectedConsultations, setSelectedConsultations] = useState<string[]>(
    [],
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [dateFilter, setDateFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const prevConsultationsRef = useRef(consultations);
  const itemsPerPage = 5;
  // ✅ ADD this useEffect to detect consultations changes
  useEffect(() => {
    // Check if consultations reference changed or length changed
    if (
      prevConsultationsRef.current !== consultations ||
      prevConsultationsRef.current.length !== consultations.length
    ) {
      setSelectedConsultations([]);
      setCurrentPage(1);
      prevConsultationsRef.current = consultations;
    }
  }, [consultations]);

  // ✅ ADD cleanup on unmount
  useEffect(() => {
    return () => {
      setSelectedConsultations([]);
      if (resetConsultationsState) {
        resetConsultationsState();
      }
    };
  }, [resetConsultationsState]);

  // Helper function to parse consultation date with error handling
  const parseConsultationDate = (dateStr: string): Date | null => {
    try {
      // Handle DD/MM/YY format (e.g., "15/01/24")
      const [day, month, year] = dateStr.split("/");
      if (!day || !month || !year) return null;

      // Convert 2-digit year to 4-digit (assuming 2000s)
      const fullYear = year.length === 2 ? `20${year}` : year;

      // Create date object (month is 0-indexed)
      const date = new Date(
        parseInt(fullYear),
        parseInt(month) - 1,
        parseInt(day),
      );

      // Validate the date
      if (isNaN(date.getTime())) return null;

      return date;
    } catch {
      return null;
    }
  };

  // Pre-compute consultation dates for better performance
  const consultationsWithDates = useMemo(() => {
    return consultations.map((consultation) => ({
      ...consultation,
      parsedDate:
        consultation.completedDate != null
          ? parseConsultationDate(consultation.completedDate)
          : parseConsultationDate(consultation.date),
    }));
  }, [consultations]);

  // Filter consultations based on selected filters
  const filteredConsultations = useMemo(() => {
    let filtered = consultationsWithDates;

    filtered = filtered.filter(
      (consultation) => consultation.consultationStatus === 1,
    );
    // Apply date filter
    if (dateFilter !== "all") {
      const now = new Date();
      let filterDate: Date;

      switch (dateFilter) {
        case "today":
          filterDate = new Date();
          filterDate.setHours(0, 0, 0, 0);
          break;
        case "7days":
          filterDate = new Date();
          filterDate.setDate(now.getDate() - 7);
          filterDate.setHours(0, 0, 0, 0);
          break;
        case "30days":
          filterDate = new Date();
          filterDate.setDate(now.getDate() - 30);
          filterDate.setHours(0, 0, 0, 0);
          break;
        case "90days":
          filterDate = new Date();
          filterDate.setDate(now.getDate() - 90);
          filterDate.setHours(0, 0, 0, 0);
          break;
        default:
          filterDate = new Date(0); // Fallback
      }

      filtered = filtered.filter((consultation) => {
        if (!consultation.parsedDate) return false;
        return consultation.parsedDate >= filterDate;
      });
    }

    if (typeFilter !== "all") {
      filtered = filtered.filter(
        (consultation) => consultation.serviceName === typeFilter,
      );
    }
    filtered = filtered.sort((a, b) => {
      const dateA = new Date(a.createdOn || 0).getTime();
      const dateB = new Date(b.createdOn || 0).getTime();
      return dateB - dateA;
    });
    // Remove the parsedDate property before returning
    return filtered.map(({ parsedDate, ...consultation }) => consultation);
  }, [consultationsWithDates, dateFilter, typeFilter]);

  // Calculate pagination based on filtered results
  const totalPages = Math.ceil(filteredConsultations.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentConsultations = filteredConsultations.slice(
    startIndex,
    endIndex,
  );
  const handleSelectConsultationAndClear = (consultation: Consultation) => {
    setStreamedText("");
    setSnomedCodes([]);
    onSelectConsultation(consultation);
  };

  // Reset selections when changing pages
  const handlePageChange = React.useCallback((page: number) => {
    setCurrentPage(page);
    setSelectedConsultations([]);
  }, []);

  // Reset pagination and selections when filters change
  const handleDateFilterChange = React.useCallback((value: string) => {
    setDateFilter(value);
    setCurrentPage(1);
    setSelectedConsultations([]);
  }, []);

  const handleTypeFilterChange = React.useCallback((value: string) => {
    setTypeFilter(value);
    setCurrentPage(1);
    setSelectedConsultations([]);
  }, []);

  // Memoized clear filters function
  const handleClearFilters = React.useCallback(() => {
    setDateFilter("all");
    setTypeFilter("all");
    setCurrentPage(1);
    setSelectedConsultations([]);
  }, []);

  const handleConsultationSelect = useCallback(
    (consultationId: string, checked: boolean) => {
      if (checked) {
        setSelectedConsultations((prev) =>
          prev.includes(consultationId) ? prev : [...prev, consultationId],
        );
      } else {
        setSelectedConsultations((prev) =>
          prev.filter((id) => id !== consultationId),
        );
      }
    },
    [],
  );

  const handleSelectAll = React.useCallback(
    (checked: boolean | "indeterminate") => {
      if (checked === true || checked === "indeterminate") {
        setSelectedConsultations(
          currentConsultations?.map((c) => c.consultationId),
        );
      } else {
        setSelectedConsultations([]);
      }
    },
    [currentConsultations],
  );

  const handleSummarise = () => {
    summarizeConsultationSummaries({
      consultationIds: selectedConsultations,
    }).then((res) => {
      setIsSummaryModalOpen(true);
    });
  };

  const handleCloseSummaryModal = React.useCallback(() => {
    setIsSummaryModalOpen(false);
  }, []);

  // Get selected consultation data for the modal
  const selectedConsultationData = React.useMemo(() => {
    return filteredConsultations.filter((c) =>
      selectedConsultations.includes(c.consultationId),
    );
  }, [filteredConsultations, selectedConsultations]);

  const isAllSelected =
    currentConsultations.length > 0 &&
    selectedConsultations.length === currentConsultations.length;
  const isPartiallySelected =
    selectedConsultations.length > 0 &&
    selectedConsultations.length < currentConsultations.length;

  const renderPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5; // how many pages around the current one

    if (totalPages <= maxVisible) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (currentPage > 3) {
      pages.push(1, "…");
    }

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (currentPage < totalPages - 2) {
      pages.push("…", totalPages);
    }

    return pages;
  };
  const distinctServices = useMemo(() => {
    const names = consultations
      .map((c) => c.serviceName)
      .filter((name): name is string => !!name); // remove null/undefined

    return Array.from(new Set(names));
  }, [consultations]);

  return (
    <div
      className={`rounded-xl p-4 transition-colors duration-300 ${
        isDarkMode ? "bg-gray-800" : "bg-white"
      } shadow-sm`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users
            className={`w-4 h-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
          />
          <h3
            className={`text-sm font-semibold transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Recent Consultations
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-xs">
            {filteredConsultations.length}/{consultations.length}
          </Badge>
          {currentConsultations.length > 0 && (
            <div className="flex items-center gap-1">
              <Checkbox
                id="select-all"
                checked={isPartiallySelected ? "indeterminate" : isAllSelected}
                onCheckedChange={handleSelectAll}
                className="w-4 h-4"
              />
              <label
                htmlFor="select-all"
                className={`text-xs cursor-pointer transition-colors duration-300 ${
                  isDarkMode ? "text-gray-300" : "text-gray-600"
                }`}
              >
                All
              </label>
            </div>
          )}
        </div>
      </div>

      {/* Filter Controls */}
      <div
        className={`mb-4 p-3 rounded-lg border transition-colors duration-300 ${
          isDarkMode
            ? "bg-gray-700/50 border-gray-600"
            : "bg-gray-50 border-gray-200"
        }`}
      >
        <div className="relative flex flex-wrap items-center gap-3 overflow-visible">
          {/* Filters Label */}
          <div className="flex items-center gap-2">
            <Filter
              className={`w-4 h-4 ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
            />
            <span
              className={`text-xs font-medium transition-colors duration-300 ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Filters:
            </span>
          </div>

          {/* Date Filter */}
          <div className="flex flex-wrap items-center gap-2">
            <label
              className={`text-xs transition-colors duration-300 ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              Date:
            </label>
            <Select value={dateFilter} onValueChange={handleDateFilterChange}>
              <SelectTrigger
                className={`w-32 h-7 text-xs transition-colors duration-300 ${
                  isDarkMode
                    ? "bg-gray-800 border-gray-600 text-white hover:bg-gray-700"
                    : "bg-white border-gray-300 hover:bg-gray-50"
                }`}
              >
                <SelectValue placeholder="All Time" />
              </SelectTrigger>
              <SelectContent position="popper" sideOffset={4}>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="7days">Last 7 Days</SelectItem>
                <SelectItem value="30days">Last 30 Days</SelectItem>
                <SelectItem value="90days">Last 90 Days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Type Filter */}
          <div className="flex flex-wrap items-center gap-2">
            <label
              className={`text-xs transition-colors duration-300 ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              Type:
            </label>
            <Select value={typeFilter} onValueChange={handleTypeFilterChange}>
              <SelectTrigger
                className={`w-40 h-7 text-xs transition-colors duration-300 ${
                  isDarkMode
                    ? "bg-gray-800 border-gray-600 text-white hover:bg-gray-700"
                    : "bg-white border-gray-300 hover:bg-gray-50"
                }`}
              >
                <SelectValue placeholder="All Types" />
              </SelectTrigger>

              <SelectContent
                position="popper"
                sideOffset={4}
                className="max-h-64 z-50 w-[var(--radix-select-trigger-width)]"
              >
                <SelectItem value="all">All Types</SelectItem>
                {distinctServices.map((service) => (
                  <SelectItem key={service} value={service}>
                    {service}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Clear Filters */}
          {(dateFilter !== "all" || typeFilter !== "all") && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleClearFilters}
              className="px-2 text-xs h-7 whitespace-nowrap"
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {selectedConsultations.length > 0 && (
        <div
          className={`mb-4 p-3 rounded-lg border transition-colors duration-300 ${
            isDarkMode
              ? "bg-blue-900/20 border-blue-800"
              : "bg-blue-50 border-blue-200"
          }`}
        >
          <div className="flex items-center justify-between">
            <span
              className={`text-sm font-medium transition-colors duration-300 ${
                isDarkMode ? "text-blue-300" : "text-blue-700"
              }`}
            >
              {selectedConsultations.length} consultation
              {selectedConsultations.length !== 1 ? "s" : ""} selected
            </span>
            <Button
              size="sm"
              onClick={handleSummarise}
              disabled={isConsultationPointsLoading}
              className="text-white transition-all duration-300 shadow-lg bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600 hover:from-purple-700 hover:via-blue-700 hover:to-cyan-700 hover:shadow-xl disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isConsultationPointsLoading ? (
                <>
                  <div className="w-4 h-4 mr-1 border-2 border-white rounded-full border-t-transparent animate-spin" />
                  Summarising...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-1" />
                  Summarise
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {showPatientConsultationsLoader ? (
          // ✅ Skeleton loaders
          [1, 2, 3, 4, 5].map((i) => (
            <Box
              key={i}
              className={`border rounded-lg p-3 ${
                isDarkMode ? "border-gray-600" : "border-gray-200"
              }`}
            >
              <div className="flex items-start gap-3">
                {/* Checkbox placeholder */}
                <Skeleton
                  variant="circular"
                  width={16}
                  height={16}
                  className="mt-1"
                />
                <div className="flex-1 space-y-2">
                  <Skeleton variant="text" width="40%" height={16} />
                  <Skeleton variant="text" width="30%" height={14} />
                  <Skeleton variant="text" width="50%" height={14} />
                </div>
              </div>
            </Box>
          ))
        ) : currentConsultations.length === 0 ? (
          <div
            className={`text-center py-8 transition-colors duration-300 ${
              isDarkMode ? "text-gray-400" : "text-gray-500"
            }`}
          >
            <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="mb-1 text-sm font-medium">
              {filteredConsultations.length === 0 &&
              (dateFilter !== "all" || typeFilter !== "all")
                ? "No consultations match your filters"
                : "No consultations found"}
            </p>
            {filteredConsultations.length === 0 &&
              (dateFilter !== "all" || typeFilter !== "all") && (
                <p className="text-xs opacity-75">
                  Try adjusting your filters or clearing them to see more
                  results.
                </p>
              )}
          </div>
        ) : (
          currentConsultations.map((consultation) => {
            const isSelected = selectedConsultations.includes(
              consultation.consultationId,
            );
            return (
              <div
                key={consultation.consultationId}
                className={`group border rounded-lg p-3 transition-colors duration-300 ${
                  isDarkMode ? "border-gray-600" : "border-gray-200"
                } ${
                  isSelected
                    ? isDarkMode
                      ? "bg-blue-900/20 border-blue-700"
                      : "bg-blue-50 border-blue-300"
                    : isDarkMode
                      ? "hover:bg-gray-700"
                      : "hover:bg-gray-50"
                }`}
              >
                <div className="flex items-start gap-3">
                  <Checkbox
                    id={`consultation-${consultation.consultationId}`}
                    checked={isSelected}
                    onCheckedChange={(checked) =>
                      handleConsultationSelect(
                        consultation.consultationId,
                        checked === true,
                      )
                    }
                    className="w-4 h-4 mt-1"
                  />
                  <div
                    className="flex-1 cursor-pointer"
                    onClick={(e) => {
                      // Prevent triggering when clicking on checkbox area
                      const target = e.target as HTMLElement;
                      if (
                        !target.closest('[role="checkbox"]') &&
                        !target.closest("button")
                      ) {
                        handleSelectConsultationAndClear(consultation);
                      }
                    }}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span
                        className={`text-xs font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {consultation.type}
                      </span>
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-xs transition-colors duration-300 ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          {consultation.completedTime &&
                          consultation.completedTime !== null
                            ? consultation.completedTime
                            : consultation.time}
                        </span>
                        <div className="transition-opacity opacity-0 group-hover:opacity-100">
                          <Eye
                            className="w-3 h-3 text-blue-500"
                            title="Preview Consultation"
                          />
                        </div>
                      </div>
                    </div>
                    <p
                      className={`text-xs transition-colors duration-300 ${
                        isDarkMode ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      {consultation.pharmacist}
                    </p>
                    <p
                      className={`text-xs transition-colors duration-300 ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      {consultation.completedDate &&
                      consultation.completedDate !== null
                        ? consultation.completedDate
                        : consultation.date}
                    </p>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination Controls */}
      {filteredConsultations.length > 0 && totalPages > 1 && (
        <div
          className={`mt-4 pt-3 border-t flex items-center justify-between transition-colors duration-300 ${
            isDarkMode ? "border-gray-600" : "border-gray-200"
          }`}
        >
          <div
            className={`text-xs transition-colors duration-300 ${
              isDarkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            Showing {startIndex + 1}-
            {Math.min(endIndex, filteredConsultations.length)} of{" "}
            {filteredConsultations.length}
            {filteredConsultations.length !== consultations.length && (
              <span
                className={`ml-1 transition-colors duration-300 ${
                  isDarkMode ? "text-gray-500" : "text-gray-500"
                }`}
              >
                (filtered from {consultations.length})
              </span>
            )}
          </div>

          <div className="flex items-center gap-1">
            {/* Previous Button */}
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`p-1 rounded transition-colors duration-300 ${
                currentPage === 1
                  ? isDarkMode
                    ? "text-gray-600 cursor-not-allowed"
                    : "text-gray-400 cursor-not-allowed"
                  : isDarkMode
                    ? "text-gray-300 hover:bg-gray-700"
                    : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            {/* Page Numbers */}
            {renderPageNumbers().map((page, idx) =>
              page === "…" ? (
                <span
                  key={`ellipsis-${idx}`}
                  className="px-2 py-1 text-xs text-gray-400 select-none"
                >
                  …
                </span>
              ) : (
                <button
                  key={page}
                  onClick={() => handlePageChange(page as number)}
                  className={`px-2 py-1 text-xs rounded transition-colors duration-300 ${
                    page === currentPage
                      ? isDarkMode
                        ? "bg-blue-600 text-white"
                        : "bg-blue-500 text-white"
                      : isDarkMode
                        ? "text-gray-300 hover:bg-gray-700"
                        : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  {page}
                </button>
              ),
            )}

            {/* Next Button */}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`p-1 rounded transition-colors duration-300 ${
                currentPage === totalPages
                  ? isDarkMode
                    ? "text-gray-600 cursor-not-allowed"
                    : "text-gray-400 cursor-not-allowed"
                  : isDarkMode
                    ? "text-gray-300 hover:bg-gray-700"
                    : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* AI Summary Modal */}
      <ConsultationSummaryModal
        isOpen={isSummaryModalOpen}
        onClose={handleCloseSummaryModal}
        consultations={selectedConsultationData}
        isDarkMode={isDarkMode}
      />
    </div>
  );
}
